/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import UserTable from './UserTable';
import { UserInvitationForm } from './UserInvitationForm';
import { Loader } from '@g17eco/atoms/loader';
import NotAuthorised from '../../routes/not-authorised';
import { generateUrl } from '../util';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import UserInvitationFormContainer from './UserInvitationFormContainer';
import { filterValidRoles, UserFilterReportingLevel, UserRoles } from '../../constants/user';
import './styles.scss';
import { getFullName } from '../../utils/user';
import { OnboardingStatus } from '../../types/onboarding';
import { RouteInterfaceMin } from '../../types/routes';
import { UserLimitUsage } from '../../apps/company-tracker/components/user-limit/UserLimitTypes';
import { Button } from 'reactstrap';
import { AdminBreadcrumbsProps } from '@g17eco/molecules/breadcrumbs';
import { BasicAlert } from '@g17eco/molecules/alert';
import { Option } from '@g17eco/molecules/select/SelectFactory';
import { InitiativeUser, useGetInitiativeUsersQuery } from '@api/manage-users';
import { BulkOnboardingContainer } from '../../apps/company-tracker/components/bulk-onboarding-import';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { UserPermissions } from '../../types/user';
import { UserTableUser } from './types';

const getNearestPermission = (userPermissions: UserPermissions[], levelIds: string[]) => {
  for (const id of levelIds) {
    const permission = userPermissions.find((p) => p.initiativeId === id);
    if (permission) {
      return permission;
    }
  }
  return undefined;
};

// Roles and managed-on levels: this level, else the nearest parent with a permission, else subsidiaries
const getLevelAccess = (
  userPermissions: UserPermissions[],
  data: InitiativeUser,
  initiativeId: string
): Pick<UserTableUser, 'roles' | 'isInherited' | 'inheritedFrom' | 'managedOn' | 'managedOnSubsidiaries'> => {
  const source = getNearestPermission(userPermissions, [initiativeId, ...data.parentInitiativeIds]);
  if (!source) {
    // No permission here or above: subsidiaries only, or none listed
    const permissionIds = new Set(userPermissions.map((p) => p.initiativeId));
    return {
      roles: [],
      isInherited: true,
      managedOn: data.subsidiaries?.filter((l) => permissionIds.has(l._id)),
      managedOnSubsidiaries: true,
    };
  }
  const roles = source.permissions.filter(filterValidRoles);
  if (source.initiativeId === initiativeId) {
    return { roles, isInherited: false };
  }
  const level = data.parentInitiatives?.find((l) => l._id === source.initiativeId) ?? { _id: source.initiativeId };
  return {
    roles,
    isInherited: true,
    inheritedFrom: level.name,
    managedOn: [level],
  };
};

const getConsolidateUsers = (data: InitiativeUser | undefined, initiativeId: string): UserTableUser[] => {
  const userTableUsers: UserTableUser[] = [];

  if (!data) {
    return userTableUsers;
  }

  data.users.forEach(user => {
    userTableUsers.push({
      _id: user._id,
      isStaff: user.isStaff ?? false,
      isActivated: true,
      name: getFullName(user, user._id),
      email: user.email,
      ...getLevelAccess(user.permissions, data, initiativeId),
      lastUpdated: user.lastLogin,
      lastLogin: user.lastLogin,
      isRequestToJoin: false,
      user,
      invitedDate: user.invitedDate,
    });
  });

  data.invitations.forEach(invitation => {
    const isRequestToJoin = invitation.status === OnboardingStatus.NotStarted;
    const user = invitation.user;
    const emailPrefix = user.email.split('@')[0];
    // Permissions on no listed level fall back to the onboarding's level, where the invitation is managed
    const permissionAccess = getLevelAccess(user.permissions, data, initiativeId);
    const access = permissionAccess.managedOnSubsidiaries && !permissionAccess.managedOn?.length
      ? {
        ...getLevelAccess([{ initiativeId: invitation.initiativeId, permissions: [] }], data, initiativeId),
        // No role comes from the onboarding's level
        inheritedFrom: undefined,
      }
      : permissionAccess;
    userTableUsers.push({
      _id: invitation._id,
      isStaff: false,
      isActivated: false,
      name: isRequestToJoin ? getFullName(user, emailPrefix) : emailPrefix,
      email: user.email,
      ...access,
      // Invitations sent from this level are managed here
      isInherited: access.isInherited && invitation.initiativeId !== initiativeId,
      lastUpdated: invitation.created,
      lastLogin: '',
      isRequestToJoin,
      invitation,
    });
  });

  return userTableUsers;
};

interface UserManagementProps {
  initiativeId: string;
  page?: string;
  userLimit: number;
  workgroupsLimit?: number;
  UserLimitComponent?: React.ElementType<UserLimitUsage>;
  baseRoute: RouteInterfaceMin;
  buttons?: React.JSX.Element[];
  canInviteMultipleUsers?: boolean;
  BreadcrumbsComponent: (props: AdminBreadcrumbsProps) => JSX.Element;
}

interface UserLimitUpgradeParams extends Pick<UserManagementProps, 'UserLimitComponent' | 'userLimit'> {
  limitReached: boolean;
  currentUsage: number;
}

const UserLimitUpgrade = ({ limitReached, currentUsage, UserLimitComponent, userLimit }: UserLimitUpgradeParams) => {
  if (!limitReached) {
    return null;
  }

  if (UserLimitComponent) {
    return <UserLimitComponent currentUsage={currentUsage} userLimit={userLimit} />;
  }

  return (
    <BasicAlert type='warning'>
      You have reached your user limit of {userLimit}. Please contact us to increase your limit.
    </BasicAlert>
  );
};

export const UserManagement = (props: UserManagementProps) => {
  const {
    userLimit,
    initiativeId,
    page,
    baseRoute,
    UserLimitComponent,
    canInviteMultipleUsers = false,
    BreadcrumbsComponent,
    workgroupsLimit = 0,
  } = props;

  const history = useHistory();
  const location = useLocation();

  const [filterReportingLevel, setFilterReportingLevel] = useState<UserFilterReportingLevel>('inherited');
  const { data, isFetching, refetch, isError } = useGetInitiativeUsersQuery({
    initiativeId,
    includeSubsidiaries: filterReportingLevel === 'inherited_and_subsidiaries'
  });

  const handleReportingLevelChange = (
    filter: Option<UserFilterReportingLevel> | Option<UserFilterReportingLevel>[]
  ) => {
    if (Array.isArray(filter)) {
      return;
    }
    setFilterReportingLevel(filter?.value ?? undefined);
  };

  const listUsers = useMemo(() => {
    return getConsolidateUsers(data, initiativeId).reduce(
      (accumulator, user) => {
        if (user.roles.includes(UserRoles.Owner)) {
          accumulator.hasOwner = true;
        }

        if (user.isStaff) {
          accumulator.staff.push(user);
          return accumulator;
        }
        accumulator.users.push(user);
        return accumulator;
      },
      { users: [], staff: [], hasOwner: false } as { hasOwner: boolean; users: UserTableUser[]; staff: UserTableUser[] }
    );
  }, [data, initiativeId]);

  // Stable, as UserTable's columns depend on it
  const handleReload = useCallback(async () => {
    refetch();
  }, [refetch]);

  // In ?search= so card links can pre-fill it and a reload keeps it
  const search = new URLSearchParams(location.search).get('search') ?? '';
  const handleSearchChange = (value: string) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set('search', value);
    } else {
      searchParams.delete('search');
    }
    history.replace({ ...location, search: searchParams.toString() });
  };

  if (!initiativeId || isFetching) {
    return <Loader />;
  }

  if (isError) {
    return <NotAuthorised />
  }

  const nonStaffUserCount = listUsers.users.length;
  const limitReached = userLimit > 0 && nonStaffUserCount >= userLimit;

  const toggleUserInvitationForm = () => {
    const invitationUrl = generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId, page: 'invite' });
    history.push(invitationUrl);
  };

  const onClickInviteMultipleUsers = () => {
    const url = generateUrl(baseRoute, { initiativeId, page: 'import-export' });
    return history.push(url);
  };

  const baseBreadcrumb = {
    label: 'Manage Users',
    url: generateUrl(baseRoute, { initiativeId, portfolioId: initiativeId }),
  };

  if (page === 'invite') {
    return (
      <Dashboard className='userManagement'>
        <DashboardRow>
          <BreadcrumbsComponent initiativeId={initiativeId} breadcrumbs={[baseBreadcrumb, { label: 'Invite user' }]} />
        </DashboardRow>
        <DashboardSectionTitle title={'Invite user(s)'} buttons={props.buttons} />
        <DashboardSection className='userManagement-container'>
          <UserInvitationFormContainer>
            <UserInvitationForm baseRoute={baseRoute} initiativeId={initiativeId} handleReload={handleReload} />
          </UserInvitationFormContainer>
        </DashboardSection>
      </Dashboard>
    );
  }

  if (page === 'import-export' && canInviteMultipleUsers) {
    const breadcrumbs = (
      <BreadcrumbsComponent
        initiativeId={initiativeId}
        breadcrumbs={[baseBreadcrumb, { label: 'Invite multiple users' }]}
      />
    );
    return (
      <BulkOnboardingContainer
        initiativeId={initiativeId}
        userLimit={userLimit}
        BreadcrumbsComponent={breadcrumbs}
      />
    );
  }

  const renderManageUserButtons = () => {
    if (!canInviteMultipleUsers) {
      return props.buttons;
    }

    const internalButtons = [
      <Button key='invite-btn' onClick={onClickInviteMultipleUsers} disabled={limitReached} color='transparent'>
        <i className='fal fa-users-medical mr-2' />
        Mass invite users
      </Button>,
    ];

    if (workgroupsLimit > 0) {
      internalButtons.push(
        <Button
          key='manage-workgroups-btn'
          onClick={() => history.push(generateUrl(baseRoute, { initiativeId, page: 'workgroups' }))}
          outline={false}
          className='ms-2'
        >
          <i className='fal fa-users mr-2' />
          Manage workgroups
          <FeatureStability stability='beta' />
        </Button>,
      );
    }

    return [...(props.buttons ?? []), ...internalButtons];
  };

  return (
    <Dashboard className='userManagement'>
      <DashboardRow>
        <BreadcrumbsComponent initiativeId={initiativeId} breadcrumbs={[baseBreadcrumb]} />
      </DashboardRow>
      <DashboardSectionTitle title={'Manage users'} buttons={renderManageUserButtons()} />
      <DashboardSection className='userManagement-container'>
        <UserLimitUpgrade
          currentUsage={nonStaffUserCount}
          userLimit={userLimit}
          limitReached={limitReached}
          UserLimitComponent={UserLimitComponent}
        />
        <div data-testid='manage-users-table'>
          <UserTable
            users={listUsers.users}
            hasOwner={listUsers.hasOwner}
            baseRoute={baseRoute}
            handleReload={handleReload}
            toggleUserInvitationForm={toggleUserInvitationForm}
            isAddUserDisabled={limitReached}
            handleReportingLevelChange={handleReportingLevelChange}
            filterReportingLevel={filterReportingLevel}
            isDownloadable
            search={search}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className='mt-3 text-right' data-testid={'user-limit-text'}>
          {nonStaffUserCount}/{userLimit || 'Unlimited'} users
        </div>
      </DashboardSection>
      <DashboardSectionTitle title={'Support users (G17Eco staff)'} />
      <DashboardSection className='userManagement-container'>
        <div data-testid='support-users-table'>
          <UserTable
            users={listUsers.staff}
            hasOwner={listUsers.hasOwner}
            baseRoute={baseRoute}
            handleReload={handleReload}
            toggleUserInvitationForm={toggleUserInvitationForm}
            btnText='Add support'
            handleReportingLevelChange={handleReportingLevelChange}
            filterReportingLevel={filterReportingLevel}
          />
        </div>
      </DashboardSection>
    </Dashboard>
  );
};
